import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { procedures } from "../data/procedures";
import { Alert } from "../types";
import {
  calculateStatusCounts,
  getActionAvailability,
  getBannerAlerts,
  selectPanelAlerts as getPanelAlerts,
} from "../utils/alertHelpers";
import { AlertSlice, createAlertSlice } from "./slices/alertSlice";
import { CraneSlice, createCraneSlice } from "./slices/craneSlice";
import { createKeyboardSlice, KeyboardSlice } from "./slices/keyboardSlice";
import { createMotionSlice, MotionSlice } from "./slices/motionSlice";
import { createProcedureSlice, ProcedureSlice } from "./slices/procedureSlice";
import { createUISlice, UISlice } from "./slices/uiSlice";
import {
  createWalkthroughSlice,
  WalkthroughSlice,
} from "./slices/walkthroughSlice";
import { createWidgetSlice, WidgetSlice } from "./slices/widgetSlice";
import { devtools } from "zustand/middleware";
import { alertWidgetSuggestions } from "../data/widgets";

// Combined store type
export type AppState = AlertSlice &
  CraneSlice &
  UISlice &
  ProcedureSlice &
  KeyboardSlice &
  MotionSlice &
  WidgetSlice &
  WalkthroughSlice;

// Standalone selector functions for use with Zustand
export const selectStatusCounts = (state: AppState) =>
  calculateStatusCounts(state.alerts, state.currentProcedure);

export const selectBannerAlerts = (state: AppState) =>
  getBannerAlerts(state.alerts);

export const selectPanelAlerts = (state: AppState): Alert[] =>
  getPanelAlerts(state.alerts, state.notificationsFilter);

export const selectActionAvailability = (state: AppState) =>
  getActionAvailability(state.alerts, procedures[state.currentProcedure]);

export const selectCurrentAlert = (state: AppState) => {
  const bannerAlerts = getBannerAlerts(state.alerts);
  return bannerAlerts[state.currentAlertIndex] || null;
};

export const getSuggestedWidgetKey = (state: AppState) => {
  const currentAlert = selectCurrentAlert(state);
  if (!currentAlert) {
    return null;
  }
  const suggestedWidgetKey =
    alertWidgetSuggestions[
      currentAlert.definitionId as keyof typeof alertWidgetSuggestions
    ];
  if (!suggestedWidgetKey) {
    return null;
  }
  const suggestedWidgetIsAlreadyInUse = state.widgets.some(
    (widget) => widget.type === suggestedWidgetKey
  );
  if (suggestedWidgetIsAlreadyInUse) {
    return null;
  }
  return suggestedWidgetKey;
};

export const useStore = create<AppState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Combine all slices
      ...createAlertSlice(set, get),
      ...createCraneSlice(set, get),
      ...createUISlice(set, get),
      ...createProcedureSlice(set),
      ...createKeyboardSlice(set, get),
      ...createMotionSlice(set, get),
      ...createWidgetSlice(set, get),
      ...createWalkthroughSlice(set),
    })),
    { name: "Huisman Crane Store" }
  )
);
